import type { Owner } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { pick } from '@backmarket/utils/object/pick'
import type {
  CLSMetricWithAttribution,
  INPMetricWithAttribution,
} from 'web-vitals/attribution'

import type { PageViewData } from '../types'

const SLOW_INTERACTION_THRESHOLD = 200

/**
 * Logs slow INP (Interaction to Next Paint) metrics to Datadog.
 * Only logs events that are "needs improvement" or "poor" (>= 200ms).
 */
export function logINPAttributionToDatadog(
  metric: INPMetricWithAttribution,
  pageData: PageViewData,
): void {
  const logger = useLogger()
  const { value: duration, attribution, id, rating, name } = metric

  if (duration < SLOW_INTERACTION_THRESHOLD) return

  const [location, targetElement] = attribution.interactionTarget.split('|')
  let longestScript
  if (metric.attribution.longestScript) {
    longestScript = pick(
      metric.attribution.longestScript.entry as unknown as Record<
        string,
        unknown
      >,
      [
        'invoker',
        'invokerType',
        'name',
        'sourceURL',
        'sourceFunctionName',
        'sourceCharPosition',
      ],
    )
  }

  const analyticsData = {
    owners: [],
    web_vitals: {
      event_name: 'web_vitals_inp',
      metric_id: id,
      metric_rating: rating,
      event_type: name,
      target_element: targetElement,
      duration,
      input_delay: Math.round(attribution.inputDelay),
      processing_duration: Math.round(attribution.processingDuration),
      presentation_delay: Math.round(attribution.presentationDelay),
      page_type: pageData.pageType,
      location,
      device_type: pageData.deviceType,
      connection: pageData.connection,
      country: pageData.country,
      longestScript,
    },
  }

  logger.info('INP slow interaction', analyticsData)
}

const HIGH_LAYOUT_SHIFT_THRESHOLD = 0.1
/**
 * Logs high CLS (Cumulative Layout Shift) to Datadog.
 * Only logs events that are "needs improvement" or "poor" (>= .1).
 */
export function logCLSAttributionToDatadog(
  metric: CLSMetricWithAttribution,
  pageData: PageViewData & {
    previousRouteName?: string
    currentRouteName?: string
  },
): void {
  const logger = useLogger()
  if (!metric.attribution.largestShiftValue) return

  if (metric.attribution.largestShiftValue < HIGH_LAYOUT_SHIFT_THRESHOLD) return

  const analyticsData = {
    owners: [] as Owner[],
    web_vitals: {
      event_name: 'web_vitals_cls',
      metric_id: metric.id,
      metric_rating: metric.rating,
      event_type: metric.name,
      page_type: pageData.pageType,
      device_type: pageData.deviceType,
      connection: pageData.connection,
      country: pageData.country,
      largest_shift_value: metric.attribution.largestShiftValue,
      target_element: metric.attribution.largestShiftTarget,
      previous_route_name: pageData.previousRouteName,
      current_route_name: pageData.currentRouteName,
    },
  }

  logger.info('CLS high layout shift', analyticsData)
}
